import React, { useEffect, useMemo, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    TextField,
    Typography,
} from '@mui/material';
import { ExpandMore, Refresh } from '@mui/icons-material';
import { showToastError } from '../../utils/common/toast';
import { getDefaultSystemPrompt, getRecentSystemPrompts } from '../../utils/server/storageUtils';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import { useAuth } from '../../../context/AuthContext';

export function ShowRecentPromptsButton({ onPromptSelectAction }) {
    const { t, lang } = useAppTranslation();
    const { token } = useAuth();
    const [open, setOpen] = useState(false);
    const [prompts, setPrompts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const handleScopeChange = () => {
            setOpen(false);
            setPrompts([]);
        };

        window.addEventListener('active-chatbot-changed', handleScopeChange);
        window.addEventListener('active-tenant-changed', handleScopeChange);

        return () => {
            window.removeEventListener('active-chatbot-changed', handleScopeChange);
            window.removeEventListener('active-tenant-changed', handleScopeChange);
        };
    }, []);

    const sortedPrompts = useMemo(
        () => [...prompts].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)),
        [prompts]
    );

    const handleOpen = async () => {
        if (!token) {
            showToastError(t.pleaseLogin);
            return;
        }

        setOpen(true);
        setIsLoading(true);
        try {
            const recentPrompts = await getRecentSystemPrompts(token);
            setPrompts(recentPrompts);
        } catch (error) {
            console.error(error);
            showToastError(t.errorLoadPrompt);
            setPrompts([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => setOpen(false);

    const handleLoadDefaultPrompt = async () => {
        if (!token) return;

        try {
            const defaultPrompt = await getDefaultSystemPrompt(token);
            if (onPromptSelectAction) onPromptSelectAction(defaultPrompt);
            handleClose();
        } catch (error) {
            console.error(error);
            showToastError(t.errorLoadPrompt);
        }
    };

    return (
        <Box>
            <Button
                onClick={handleOpen}
                endIcon={isLoading ? <CircularProgress size={16} color='inherit' /> : <Refresh />}
                variant='contained'
                disabled={!token || isLoading}
                sx={{
                    borderRadius: '16px',
                    px: 2,
                    py: 1.15,
                    textTransform: 'none',
                    fontWeight: 800,
                    background: 'var(--accent-gradient)',
                    boxShadow: '0 16px 32px rgba(15, 23, 42, 0.16)',
                    '&.Mui-disabled': {
                        color: 'rgba(255,255,255,0.72)',
                        background: 'var(--button-disabled-bg)',
                    },
                }}
            >
                {t.loadRecentPrompts}
            </Button>

            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth='md'
                PaperProps={{
                    sx: {
                        borderRadius: '24px',
                        border: '1px solid var(--border-color)',
                        background: 'var(--surface-gradient)',
                        boxShadow: '0 28px 56px rgba(15, 23, 42, 0.14)',
                    },
                }}
            >
                <DialogTitle sx={{ pb: 1.25 }}>
                    <Box
                        display='flex'
                        flexDirection={{ xs: 'column', sm: 'row' }}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        justifyContent='space-between'
                        gap={1.25}
                    >
                        <Typography sx={{ fontWeight: 800, color: 'var(--text-strong)' }}>{t.recentPromptsTitle}</Typography>
                        <Button
                            size='small'
                            variant='outlined'
                            onClick={handleLoadDefaultPrompt}
                            disabled={isLoading}
                            sx={{
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 700,
                            }}
                        >
                            {t.loadDefaultPrompt}
                        </Button>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: 'var(--text-muted)', lineHeight: 1.65 }}>{t.promptDisclaimer}</Typography>
                    <Divider sx={{ my: 1.5 }} />

                    {isLoading && (
                        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress size={28} />
                        </Box>
                    )}

                    {!isLoading && sortedPrompts.length === 0 && (
                        <Typography sx={{ color: 'var(--text-muted)', py: 1.5 }}>
                            {t.noSavedPromptHistory}
                        </Typography>
                    )}

                    {!isLoading &&
                        sortedPrompts.map((promptEntry, index) => (
                            <Accordion
                                key={`${promptEntry.timestamp?.toISOString?.() || index}-${index}`}
                                disableGutters
                                sx={{
                                    mb: 1,
                                    borderRadius: '16px !important',
                                    overflow: 'hidden',
                                    border: '1px solid var(--border-color)',
                                    boxShadow: 'none',
                                    '&:before': { display: 'none' },
                                }}
                            >
                                <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'var(--text-muted)' }} />} sx={{ backgroundColor: 'var(--surface-muted)' }}>
                                    <Typography sx={{ color: 'var(--text-soft)', fontWeight: 700 }}>
                                        {t.promptFrom}{' '}
                                        {new Date(promptEntry.timestamp).toLocaleString(lang, {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                        })}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <TextField
                                        value={promptEntry.content}
                                        multiline
                                        fullWidth
                                        InputProps={{ readOnly: true }}
                                        variant='outlined'
                                        minRows={3}
                                        label={t.promptLabel}
                                        sx={{
                                            mb: 1,
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '16px',
                                                backgroundColor: 'var(--surface-fill-strong)',
                                            },
                                        }}
                                    />
                                    {onPromptSelectAction && (
                                        <Button
                                            size='small'
                                            variant='outlined'
                                            sx={{ mt: 1, borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}
                                            onClick={() => {
                                                onPromptSelectAction(promptEntry.content);
                                                handleClose();
                                            }}
                                        >
                                            {t.loadThisPrompt}
                                        </Button>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        ))}
                </DialogContent>
            </Dialog>
        </Box>
    );
}
